import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import { isInspecaoAuditoriaFaixa, isInspecaoMtBt } from 'utils';
import FileService from '../../../../services/FileService';
import { apiErrorMessage } from '../../../../services/kmzMatriz';
import KmzCamadasPainel from './KmzCamadasPainel';
import { TIPO_COMPLETO, TIPO_CRU, TIPO_INSPECAO } from './kmzAnexoAcoes';
import { baixarParaGoogleEarth } from './kmzGoogleEarth';
import { resolverAtivos } from './kmzViewUrl';
import { anotarColecao, useKmzConsulta } from './kmzConsulta';
import { geometriaDoArquivo } from './kmzKmlPreview';
import KmzVisualizacaoMap from './KmzVisualizacaoMap';

function mensagemAbertura(error) {
  const daApi = apiErrorMessage(error, '');
  if (daApi) return daApi;
  if (error && error.message) return error.message;
  return 'Não foi possível abrir o KMZ no mapa.';
}

// O estado raiz do Redux é persistido em JSON, então um Blob que passou por ele volta como
// objeto simples ({ name, lastModifiedDate }), sem arrayBuffer. Só arquivo legível serve de local.
function arquivoLegivel(arquivo) {
  if (!arquivo) return false;
  return typeof arquivo.arrayBuffer === 'function' || (typeof Blob !== 'undefined' && arquivo instanceof Blob);
}

function blobDaResposta(data, nome) {
  const blob = typeof Blob !== 'undefined' && data instanceof Blob ? data : new Blob([data]);
  if (!blob.name) blob.name = nome;
  return blob;
}

function escolhidoDe(itens, camadasMarcadas) {
  const ativos = resolverAtivos(itens, camadasMarcadas);
  return [TIPO_COMPLETO, TIPO_INSPECAO, TIPO_CRU].find((tipo) => ativos[tipo]) || '';
}

function unirLimites(caixas) {
  let minLng = Infinity;
  let minLat = Infinity;
  let maxLng = -Infinity;
  let maxLat = -Infinity;
  caixas.forEach((bounds) => {
    if (!bounds) return;
    const [[lngA, latA], [lngB, latB]] = bounds;
    if (lngA < minLng) minLng = lngA;
    if (latA < minLat) minLat = latA;
    if (lngB > maxLng) maxLng = lngB;
    if (latB > maxLat) maxLat = latB;
  });
  if (!Number.isFinite(minLng)) return null;
  return [[minLng, minLat], [maxLng, maxLat]];
}

function montarColecao(geos, ativos) {
  if (!Object.keys(geos).length) return null;
  const features = [];
  const caixas = [];
  const icones = {};
  let truncado = false;
  Object.keys(ativos).forEach((tipo) => {
    if (!ativos[tipo]) return;
    const geo = geos[tipo];
    if (!geo || !geo.collection) return;
    if (geo.truncado) truncado = true;
    if (geo.bounds) caixas.push(geo.bounds);
    Object.assign(icones, geo.icones);
    (geo.collection.features || []).forEach((feature) => {
      features.push({
        type: 'Feature',
        geometry: feature.geometry,
        properties: { ...(feature.properties || {}), camada: tipo },
      });
    });
  });
  const bounds = unirLimites(caixas);
  return {
    collection: { type: 'FeatureCollection', features },
    bounds,
    pontoUnico: Boolean(bounds) && bounds[0][0] === bounds[1][0] && bounds[0][1] === bounds[1][1],
    truncado,
    icones,
  };
}

const KmzArquivoMapDialog = ({
  open,
  onClose,
  itens,
  camadasMarcadas,
  onCamadasChange,
  arquivoInspecao,
  nsirId,
  nomeBase,
  tipoServicoId,
}) => {
  const consultaHabilitada = isInspecaoMtBt(tipoServicoId);
  const arquivoLocal = arquivoLegivel(arquivoInspecao) ? arquivoInspecao : null;
  const lista = Array.isArray(itens) ? itens : [];
  const listaRef = useRef(lista);
  const camadasRef = useRef(camadasMarcadas);
  listaRef.current = lista;
  camadasRef.current = camadasMarcadas;
  const chaveLista = lista.map((item) => `${item.tipo}:${item.url || 'local'}`).join('|');
  const chaveCamadas = camadasMarcadas == null ? '*' : camadasMarcadas.join(',');
  const [geos, setGeos] = useState({});
  const [ativos, setAtivos] = useState({});
  const [erros, setErros] = useState({});
  const [carregando, setCarregando] = useState(false);
  const [fullScreen, setFullScreen] = useState(false);
  const [painelRecolhido, setPainelRecolhido] = useState(false);
  const [abrindoEarth, setAbrindoEarth] = useState(false);
  const [erroEarth, setErroEarth] = useState('');
  const [mapaOcupado, setMapaOcupado] = useState(false);
  const mapaOcupadoRef = useRef(false);
  const camadaTimerRef = useRef(null);
  const arquivosRef = useRef({});
  const definirMapaOcupado = (ocupado) => {
    mapaOcupadoRef.current = Boolean(ocupado);
    setMapaOcupado(Boolean(ocupado));
  };
  const anotados = useMemo(() => {
    const saida = {};
    Object.keys(geos).forEach((tipo) => {
      const geo = geos[tipo];
      if (!geo || !geo.collection) return;
      saida[tipo] = { ...geo, collection: anotarColecao(geo.collection, tipo) };
    });
    return saida;
  }, [geos]);
  const colecao = useMemo(() => montarColecao(anotados, ativos), [anotados, ativos]);
  const colecoesConsulta = useMemo(() => lista.map((item) => ({
    chave: item.tipo,
    aberto: Boolean(ativos[item.tipo]),
    collection: (anotados[item.tipo] && anotados[item.tipo].collection) || null,
  })), [lista, ativos, anotados]);
  const { consulta, camera, painel } = useKmzConsulta(
    consultaHabilitada ? colecoesConsulta : [],
    `${open}:${chaveLista}`,
  );
  const erroGeral = !carregando && !Object.keys(geos).length
    ? (Object.values(erros).find(Boolean) || '')
    : '';

  useEffect(() => {
    if (open) return;
    setGeos({});
    setAtivos({});
    setErros({});
    setCarregando(false);
    setFullScreen(false);
    setPainelRecolhido(false);
    setAbrindoEarth(false);
    setErroEarth('');
    mapaOcupadoRef.current = false;
    setMapaOcupado(false);
    if (camadaTimerRef.current) clearTimeout(camadaTimerRef.current);
    arquivosRef.current = {};
  }, [open]);

  useEffect(() => () => {
    if (camadaTimerRef.current) clearTimeout(camadaTimerRef.current);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const atuais = listaRef.current;
    let vivo = true;
    const escolhido = escolhidoDe(atuais, camadasRef.current);
    arquivosRef.current = {};
    setGeos({});
    setErros({});
    setCarregando(atuais.length > 0);
    let pendentes = atuais.length;
    const tarefas = atuais.map(async (item) => {
      const inspecaoLocal = item.tipo === TIPO_INSPECAO && !item.url && arquivoLocal;
      if (!item.url && !inspecaoLocal) {
        return [item.tipo, null, 'O arquivo ainda não está disponível para abrir no mapa.'];
      }
      try {
        const blob = inspecaoLocal
          ? arquivoLocal
          : await FileService.getFile(item.url)
            .then((response) => blobDaResposta(response.data, `${item.label}.kmz`))
            .catch((error) => {
              if (item.tipo === TIPO_INSPECAO && arquivoLocal) return arquivoLocal;
              throw error;
            });
        arquivosRef.current[item.tipo] = blob;
        const geo = await geometriaDoArquivo(blob, { escopo: item.tipo });
        return [item.tipo, geo, ''];
      } catch (error) {
        return [item.tipo, null, mensagemAbertura(error)];
      }
    });
    tarefas.forEach((tarefa) => {
      tarefa.then(([tipo, geo, falha]) => {
        if (!vivo) return;
        if (geo) setGeos((atual) => ({ ...atual, [tipo]: geo }));
        if (falha) setErros((atual) => ({ ...atual, [tipo]: falha }));
        pendentes -= 1;
        if (tipo === escolhido || pendentes === 0) setCarregando(false);
      });
    });
    return () => {
      vivo = false;
    };
  }, [open, chaveLista, arquivoLocal]);

  useEffect(() => {
    if (!open) return;
    setAtivos(resolverAtivos(listaRef.current, camadasMarcadas));
  }, [open, chaveLista, chaveCamadas]);

  const linhas = lista.map((item) => ({
    chave: item.tipo,
    nome: item.label || item.tipo,
    aberto: Boolean(ativos[item.tipo]),
  }));
  const varios = lista.length > 1;

  const aoAbrirEarth = async () => {
    if (abrindoEarth) return;
    setAbrindoEarth(true);
    setErroEarth('');
    try {
      await baixarParaGoogleEarth({
        itens: lista,
        nsirId,
        arquivoLocal,
        nomeBase,
        arquivos: arquivosRef.current,
      });
    } catch (error) {
      setErroEarth(mensagemAbertura(error));
    } finally {
      setAbrindoEarth(false);
    }
  };

  return (
    <Dialog
      open={open}
      fullWidth
      fullScreen={fullScreen}
      maxWidth="xl"
      onClose={(_, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
        onClose();
      }}
      slotProps={{
        paper: {
          sx: {
            display: 'flex',
            flexDirection: 'column',
            height: fullScreen ? '100%' : '92vh',
            maxHeight: fullScreen ? '100%' : '92vh',
          },
        },
      }}
    >
      <DialogTitle component="div" sx={{ pr: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h6">Visualização do KMZ</Typography>
          <IconButton
            aria-label="Fechar"
            onClick={onClose}
            sx={{ ml: 'auto', flexShrink: 0 }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent
        dividers
        sx={{ display: 'flex', flex: 1, minHeight: 0, p: 0, position: 'relative' }}
      >
        <Box
          sx={{
            position: 'relative',
            flex: painelRecolhido ? '0 0 0px' : '0 0 280px',
            width: painelRecolhido ? 0 : 280,
            minWidth: 0,
            borderRight: painelRecolhido ? 0 : 1,
            borderColor: 'divider',
            bgcolor: 'background.paper',
            overflow: 'visible',
          }}
        >
          <IconButton
            size="small"
            aria-label={painelRecolhido ? 'Mostrar arquivos' : 'Recolher arquivos'}
            aria-expanded={!painelRecolhido}
            onClick={() => setPainelRecolhido((atual) => !atual)}
            sx={{
              position: 'absolute',
              zIndex: 5,
              top: '50%',
              right: 0,
              transform: 'translate(100%, -50%)',
              width: 20,
              height: 56,
              p: 0,
              borderRadius: '0 8px 8px 0',
              bgcolor: 'background.paper',
              border: 1,
              borderLeft: 0,
              borderColor: 'divider',
              '&:hover': { bgcolor: 'background.paper' },
            }}
          >
            {painelRecolhido ? <ChevronRightIcon fontSize="small" /> : <ChevronLeftIcon fontSize="small" />}
          </IconButton>
          <Box
            sx={{
              display: painelRecolhido ? 'none' : 'flex',
              flexDirection: 'column',
              height: '100%',
            }}
          >
            <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
              <KmzCamadasPainel
                linhas={linhas}
                {...(consultaHabilitada ? painel : {})}
                ocupado={mapaOcupado}
                onToggle={(chave) => {
                  if (mapaOcupadoRef.current) return;
                  definirMapaOcupado(true);
                  if (camadaTimerRef.current) clearTimeout(camadaTimerRef.current);
                  camadaTimerRef.current = setTimeout(() => {
                    setAtivos((atual) => {
                      const proximo = { ...atual, [chave]: !atual[chave] };
                      const marcadas = listaRef.current
                        .filter((item) => proximo[item.tipo])
                        .map((item) => item.tipo);
                      if (onCamadasChange) onCamadasChange(marcadas);
                      return proximo;
                    });
                  }, 0);
                }}
              />
              {Object.values(erros).filter(Boolean).length ? (
                <Typography variant="caption" color="error" sx={{ display: 'block', px: 1.5, pt: 1 }}>
                  {Object.values(erros).find(Boolean)}
                </Typography>
              ) : null}
            </Box>
            <Box sx={{ flexShrink: 0, px: 1.5, py: 1.25, borderTop: 1, borderColor: 'divider' }}>
              <Button
                fullWidth
                size="small"
                variant="text"
                startIcon={abrindoEarth ? <CircularProgress size={14} color="inherit" /> : <DownloadOutlinedIcon />}
                disabled={abrindoEarth || !lista.length}
                onClick={aoAbrirEarth}
                sx={{ justifyContent: 'flex-start', px: 0.5, textTransform: 'none' }}
              >
                {varios ? 'Baixar todos os KMZ' : 'Baixar KMZ'}
              </Button>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', px: 0.5, lineHeight: 1.35 }}>
                {varios
                  ? 'Cada KMZ baixa em um arquivo. Abra um por vez no Google Earth.'
                  : 'Não é possível abrir direto. Baixe e abra o arquivo no Google Earth.'}
              </Typography>
              {erroEarth ? (
                <Typography variant="caption" color="error" sx={{ display: 'block', px: 0.5, pt: 0.5 }}>
                  {erroEarth}
                </Typography>
              ) : null}
            </Box>
          </Box>
        </Box>
        <Box sx={{ position: 'relative', flex: 1, minWidth: 0, minHeight: 0 }}>
          <KmzVisualizacaoMap
            ativo={open}
            geo={erroGeral ? null : colecao}
            nsirId={nsirId}
            faixa={isInspecaoAuditoriaFaixa(tipoServicoId)}
            consulta={consultaHabilitada ? consulta : null}
            camera={consultaHabilitada ? camera : null}
            fullScreen={fullScreen}
            onToggleFullScreen={() => setFullScreen((atualTela) => !atualTela)}
            ocupadoExterno={mapaOcupado}
            onOcupacao={definirMapaOcupado}
          />
          {carregando ? (
            <Stack
              alignItems="center"
              justifyContent="center"
              spacing={1}
              sx={{ position: 'absolute', inset: 0, bgcolor: 'background.paper' }}
            >
              <CircularProgress size={28} />
              <Typography variant="body2">Abrindo o KMZ no mapa</Typography>
            </Stack>
          ) : null}
          {erroGeral ? (
            <Stack
              alignItems="center"
              justifyContent="center"
              sx={{ position: 'absolute', inset: 0, px: 3, bgcolor: 'background.paper' }}
            >
              <Typography variant="body2">{erroGeral}</Typography>
            </Stack>
          ) : null}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

KmzArquivoMapDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  itens: PropTypes.arrayOf(PropTypes.shape({
    tipo: PropTypes.string,
    url: PropTypes.string,
    label: PropTypes.string,
  })),
  camadasMarcadas: PropTypes.arrayOf(PropTypes.string),
  onCamadasChange: PropTypes.func,
  arquivoInspecao: PropTypes.object,
  nsirId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  nomeBase: PropTypes.string,
  tipoServicoId: PropTypes.string,
};

KmzArquivoMapDialog.defaultProps = {
  itens: [],
  camadasMarcadas: null,
  onCamadasChange: undefined,
  arquivoInspecao: null,
  nsirId: null,
  nomeBase: '',
  tipoServicoId: '',
};

export default KmzArquivoMapDialog;
